import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Phone, Mail, MessageCircle, Menu, CalendarDays } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import QuickInquiryModal from './QuickInquiryModal';

/**
 * Sticky bottom CTA-Bar – Mobile only.
 * 5 slots: Menü · Anrufen · E-Mail (Schnellanfrage) · WhatsApp · Mietanfrage.
 * Hidden on /admin* and /mietanfrage. Slides up only after the user scrolls
 * past the mobile header (~180px), so it doesn't compete with the hero CTAs.
 */
const MobileStickyCTA = () => {
  const { settings } = useSettings();
  const { user: customerUser } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isMietanfrage = location.pathname.startsWith('/mietanfrage');
  const hideCta = isAdmin || isMietanfrage;
  const [visible, setVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    if (hideCta) document.body.classList.add('no-mobile-cta');
    else document.body.classList.remove('no-mobile-cta');
    return () => document.body.classList.remove('no-mobile-cta');
  }, [hideCta]);

  useEffect(() => {
    const onScroll = () => {
      const header = document.querySelector('header');
      const headerH = header ? header.offsetHeight : 200;
      setVisible(window.scrollY > headerH - 20);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    const open = () => setMenuOpen(true);
    const close = () => setMenuOpen(false);
    window.addEventListener('mobile-menu-open', open);
    window.addEventListener('mobile-menu-close', close);
    return () => {
      window.removeEventListener('mobile-menu-open', open);
      window.removeEventListener('mobile-menu-close', close);
    };
  }, []);

  useEffect(() => {
    if (visible && !hideCta && !menuOpen) document.body.classList.add('mobile-cta-visible');
    else document.body.classList.remove('mobile-cta-visible');
    return () => document.body.classList.remove('mobile-cta-visible');
  }, [visible, hideCta, menuOpen]);

  if (hideCta) return null;

  const contact = settings?.contact || {};
  const phoneRaw = contact.phone || '';
  const phoneHref = phoneRaw ? `tel:${phoneRaw.replace(/\s/g, '')}` : null;

  const waNumber = (contact.whatsapp_number || '').replace(/\D/g, '');
  const siteName = settings?.site_name || 'Hüpfburgen Verleih';
  const waText = encodeURIComponent(`Hallo ${siteName}, ich habe eine Frage zu Ihren Hüpfburgen.`);
  const waHref = waNumber ? `https://wa.me/${waNumber}?text=${waText}` : null;

  return (
    <>
      <div
        className="md:hidden fixed left-3 right-3 z-50 bg-white border border-neutral-200 shadow-[0_8px_28px_rgba(0,0,0,0.14)] transition-transform duration-300 ease-out overflow-hidden rounded-2xl"
        style={{
          // Layout-Viewport-basierte Positionierung — funktioniert stabil auf iOS + Android,
          // auch beim Ein-/Ausfahren der Browser-URL-Leiste.
          bottom: '6px',
          height: '48px',
          paddingBottom: 0,
          boxSizing: 'border-box',
          transform: (visible && !menuOpen) ? 'translateY(0)' : 'translateY(calc(100% + 20px))',
          willChange: 'transform',
        }}
        aria-hidden={!visible || menuOpen}
        data-testid="mobile-sticky-cta"
      >
        <div className="grid grid-cols-5 h-full">
          {/* Menü */}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('open-mobile-menu'))}
            className="flex flex-col items-center justify-center gap-0.5 text-white active:brightness-95"
            style={{ background: '#005898' }}
            aria-label="Menü öffnen"
            data-testid="sticky-menu"
          >
            <Menu className="h-4 w-4" />
            <span className="text-[10px] font-semibold leading-none">Menü</span>
          </button>

          {/* Anrufen / Buchungen */}
          {customerUser ? (
            <button
              type="button"
              onClick={() => navigate('/mein-konto')}
              className="flex flex-col items-center justify-center gap-0.5 text-sky-700 active:bg-sky-50 border-r border-neutral-100"
              aria-label="Mein Kundenkonto"
              data-testid="sticky-account"
            >
              <CalendarDays className="h-4 w-4" />
              <span className="text-[10px] font-semibold leading-none">Buchungen</span>
            </button>
          ) : phoneHref ? (
            <a
              href={phoneHref}
              className="flex flex-col items-center justify-center gap-0.5 text-sky-700 active:bg-sky-50 border-r border-neutral-100"
              aria-label="Anrufen"
              data-testid="sticky-call"
            >
              <Phone className="h-4 w-4" />
              <span className="text-[10px] font-semibold leading-none">Anrufen</span>
            </a>
          ) : (
            <a
              href={contact.email ? `mailto:${contact.email}` : '#'}
              className="flex flex-col items-center justify-center gap-0.5 text-sky-700 active:bg-sky-50 border-r border-neutral-100"
              aria-label="E-Mail"
              data-testid="sticky-email"
            >
              <Mail className="h-4 w-4" />
              <span className="text-[10px] font-semibold leading-none">E-Mail</span>
            </a>
          )}

          {/* WhatsApp */}
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-0.5 text-white"
              style={{ background: '#25D366' }}
              aria-label="WhatsApp Chat"
              data-testid="sticky-whatsapp"
            >
              <MessageCircle className="h-4 w-4" />
              <span className="text-[10px] font-semibold leading-none">WhatsApp</span>
            </a>
          )}

          {/* Kontaktformular (Schnellanfrage-Modal) */}
          <button
            type="button"
            onClick={() => setInquiryOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 text-sky-700 active:bg-sky-50 border-r border-neutral-100"
            aria-label="Kontaktformular öffnen"
            data-testid="sticky-contact-form"
          >
            <Mail className="h-4 w-4" />
            <span className="text-[10px] font-semibold leading-none">Kontakt</span>
          </button>

          {/* Mietanfrage */}
          <button
            type="button"
            onClick={() => navigate('/mietanfrage')}
            className="flex flex-col items-center justify-center gap-0.5 text-sky-900 font-semibold active:brightness-95"
            style={{ background: '#fdc20f' }}
            aria-label="Mietanfrage stellen"
            data-testid="sticky-mietanfrage"
          >
            <CalendarDays className="h-4 w-4" />
            <span className="text-[10px] leading-none">Anfrage</span>
          </button>
        </div>
      </div>

      <QuickInquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} />
    </>
  );
};

export default MobileStickyCTA;
